import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { modules, formats } from "../../utils/quillModules";
import { API_URL } from "../../utils/Url";
import { Header } from "../header/Header";
import { Sidebar } from "../sidebar/Sidebar";
import { toast } from "react-toastify";
import "./Blogs.css";

const UpdateBlogs = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const token = sessionStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaKeyword, setMetaKeyword] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [author, setAuthor] = useState("");

  /* Fetch blog */
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/blog/get_single/${slug}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const b = data.blog;
        setTitle(b.title || "");
        setContent(b.description || "");
        setImageUrl(b.blogImage?.url || b.blogImage || "");
        setMetaTitle(b.metaTitle || "");
        setMetaKeyword(b.metaKeyword || "");
        setMetaDescription(b.metaDescription || "");
        setAuthor(b.authorName || "");
      } catch (err) {
        toast.error("Failed to load blog");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchBlog();
  }, [slug, token]);

  const handleUpdate = async () => {
    if (submitting) return;

    if (!token) {
      toast.error("Unauthorized. Please login again.");
      return;
    }

    if (!title || !content || content === "<p><br></p>") {
      toast.error("Title & Description are required");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("description", content);
      formData.append("metaTitle", metaTitle);
      formData.append("metaKeyword", metaKeyword);
      formData.append("metaDescription", metaDescription);
      formData.append("authorName", author);

      if (imageFile) {
        formData.append("blogImage", imageFile);
      }

      await axios.put(`${API_URL}/api/blog/update_blog/${slug}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Blog updated successfully ✅");
      navigate("/blogs");
    } catch (err) {
      console.error("UPDATE ERROR:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to update blog ❌");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100 bg-light">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <Sidebar />
      <div className="mt-5 main-content px-3 px-md-4 py-4">
        <div className="">
          <h2 className="page-header-title">✏️ Update Article</h2>

          {/* Blog Title */}
          <div className="mb-4">
            <label className="form-label">Blog Title *</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter article title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Image Upload Area */}
          <div className="mb-4">
            <label className="form-label">Blog Cover Image</label>
            <div className="row g-3 align-items-center">
              <div className="col-md-7">
                <div className="image-upload-wrapper">
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files[0])}
                  />
                  <small className="text-muted d-block mt-2">
                    Leave unchanged to keep current cover photo
                  </small>
                </div>
              </div>

              <div className="col-md-5 text-center">
                {(imageFile || imageUrl) && (
                  <div className="image-preview-badge">
                    <img
                      src={
                        imageFile ? URL.createObjectURL(imageFile) : imageUrl
                      }
                      alt="Blog Preview"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Blog Description with Sticky Quill Editor */}
          <div className="mb-4">
            <label className="form-label">Blog Description *</label>
            <ReactQuill
              value={content}
              onChange={setContent}
              modules={modules}
              formats={formats}
              className="quill-editor-wrapper"
              placeholder="Write your article description here..."
            />
          </div>

          {/* SEO Metadata Section */}
          <div className="section-divider">
            🔍 Search Engine Optimization (SEO) Metadata
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Meta Title</label>
              <input
                type="text"
                className="form-control"
                placeholder="SEO Meta Title"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Author Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Author Name"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Meta Keywords</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. react, javascript, tutorial"
                value={metaKeyword}
                onChange={(e) => setMetaKeyword(e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Meta Description</label>
              <input
                type="text"
                className="form-control"
                placeholder="SEO Meta Description"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
              />
            </div>
          </div>

          {/* Form Action Buttons */}
          <div className="d-flex justify-content-end gap-3 mt-5">
            <button
              type="button"
              className="btn btn-cancel-custom"
              onClick={() => navigate("/blogs")}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-submit-custom"
              onClick={handleUpdate}
              disabled={submitting}
            >
              {submitting ? "Updating..." : "Update Blog"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpdateBlogs;
